import React, { useState, useRef, useEffect } from 'react';
import heic2any from 'heic2any';
import { uploadBankStatement, undoStatementUpload } from '../services/api';
import { getUserProfile } from '../services/settingsApi';
import { formatDateDisplay } from '../utils/date';
import { formatCurrency, formatNumber } from '../utils/numberFormat';

function StatementUpload({ onExpenseAdded, onBack }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userCurrency, setUserCurrency] = useState('SGD');

  useEffect(() => {
    getUserProfile().then(res => {
      if (res.profile?.default_currency_code) {
        setUserCurrency(res.profile.default_currency_code);
      }
    }).catch(() => {});
  }, []);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [fileYear, setFileYear] = useState(null);
  const [showYearPrompt, setShowYearPrompt] = useState(false);
  const [userYear, setUserYear] = useState('');
  const [lastBatchCreatedAt, setLastBatchCreatedAt] = useState(null);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Load last batch info from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('lastStatementBatch');
    if (stored) {
      try {
        const batch = JSON.parse(stored);
        setLastBatchCreatedAt(batch.batchCreatedAt);
      } catch (e) {
        console.log('Could not parse stored batch info');
      }
    }
  }, []);

  // Track elapsed time and show progress while uploading/parsing
  useEffect(() => {
    if (loading) {
      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setProgressPercent(0);

      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);

        // Progress is time-based and capped until completion
        const targetSeconds = 40;
        const pct = Math.min(95, Math.floor((elapsed / targetSeconds) * 95));
        setProgressPercent(prev => (pct > prev ? pct : prev));
      }, 500);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (progressPercent > 0) {
        setProgressPercent(100);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [loading]);

  // Helper function to extract year from filename (picks the latest year found)
  const extractYearFromFilename = (filename) => {
    if (!filename) return null;
    const yearMatches = filename.match(/\b(19|20)\d{2}\b/g);
    if (!yearMatches || yearMatches.length === 0) return null;
    return Math.max(...yearMatches.map(y => parseInt(y, 10)));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (images and PDFs)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please select an image file (JPG, PNG, GIF, WebP, HEIC) or PDF');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    setResult(null);

    // Try to extract year from filename
    const inferredYear = extractYearFromFilename(file.name);
    setFileYear(inferredYear);
    if (inferredYear) {
      console.log(`✅ Inferred year ${inferredYear} from filename: ${file.name}`);
    } else {
      console.log(`⚠️ No year found in filename: ${file.name}`);
    }

    // Create preview
    if (file.type === 'application/pdf') {
      // For PDFs, show a document icon instead of preview
      setSelectedFile(file);
      setPreview('pdf');
    } else if (file.type && (file.type.includes('heic') || file.type.includes('heif'))) {
      // For HEIC/HEIF files, convert to JPEG for preview
      try {
        setLoading(true);
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
        });

        // Create preview from converted blob
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedFile(file);
          setPreview(reader.result);
          setLoading(false);
        };
        reader.readAsDataURL(convertedBlob);
      } catch (err) {
        setError(`Failed to preview HEIC file: ${err.message}`);
        setLoading(false);
      }
    } else {
      // For images, show image preview
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // If no year inferred from filename and user hasn't provided one, prompt user
    if (!fileYear && !userYear) {
      setShowYearPrompt(true);
      return;
    }

    setLoading(true);
    setError(null);
    setShowYearPrompt(false);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const finalYear = userYear ? parseInt(userYear, 10) : fileYear;
      const response = await uploadBankStatement(selectedFile, finalYear, controller.signal);
      setResult(response);
      setUserYear(''); // Clear year input after successful upload

      // Store batch info for undo functionality
      if (response.batchCreatedAt) {
        setLastBatchCreatedAt(response.batchCreatedAt);
        localStorage.setItem('lastStatementBatch', JSON.stringify({
          batchCreatedAt: response.batchCreatedAt,
          imported: response.imported,
          timestamp: new Date().toISOString()
        }));
      }

      // Notify parent component
      if (onExpenseAdded) {
        onExpenseAdded();
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled — no error to show
      } else {
        setError(err.message);
      }
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(false);
    setFileYear(null);
    setShowYearPrompt(false);
    setUserYear('');
    setElapsedSeconds(0);
    setProgressPercent(0);
    startTimeRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUndo = async () => {
    if (!lastBatchCreatedAt) {
      setError('No statement batch to undo');
      return;
    }

    setUndoLoading(true);
    setError(null);

    try {
      const response = await undoStatementUpload(lastBatchCreatedAt);
      if (response?.error) {
        throw new Error(response.error);
      }

      // Clear the batch info from state and localStorage
      setLastBatchCreatedAt(null);
      localStorage.removeItem('lastStatementBatch');
      setShowUndoConfirm(false);

      // Show success message
      setError(null);
      alert(`✅ Successfully deleted ${response.deletedCount} transactions from the statement`);

      // Notify parent to refresh expenses
      if (onExpenseAdded) {
        onExpenseAdded();
      }
    } catch (err) {
      const message = err.message || 'Failed to undo statement upload';
      if (message.includes('most recent statement batch')) {
        setError('Undo is only available for your most recent statement upload.');
      } else if (message.includes('No statement batches found')) {
        setError('No statement upload found to undo.');
      } else {
        setError(message);
      }
    } finally {
      setUndoLoading(false);
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '600px',
      margin: '0 auto',
    },
    uploadArea: {
      border: '2px dashed var(--color-border-default)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-10) var(--space-6)',
      textAlign: 'center',
      backgroundColor: 'var(--color-bg-muted)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    uploadAreaHover: {
      borderColor: 'var(--color-primary)',
      backgroundColor: 'var(--color-primary-subtle)',
    },
    uploadIcon: {
      fontSize: '48px',
      marginBottom: 'var(--space-4)',
      display: 'block',
    },
    preview: {
      marginTop: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-md)',
    },
    previewImage: {
      width: '100%',
      maxHeight: '400px',
      objectFit: 'contain',
      backgroundColor: 'var(--color-bg-muted)',
    },
    button: {
      padding: 'var(--space-3) var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      border: 'none',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      marginTop: 'var(--space-4)',
    },
    primaryButton: {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
    },
    secondaryButton: {
      backgroundColor: 'var(--color-bg-muted)',
      color: 'var(--color-text-secondary)',
      marginLeft: 'var(--space-3)',
      border: '1px solid var(--color-border-default)',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    error: {
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error-dark)',
      marginTop: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
    },
    result: {
      marginTop: 'var(--space-8)',
      padding: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      backgroundColor: 'var(--color-success-light)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
    },
    resultItem: {
      marginBottom: 'var(--space-3)',
    },
    resultTitle: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      marginBottom: 'var(--space-4)',
      color: 'var(--color-success-dark)',
      margin: `0 0 var(--space-4) 0`,
      letterSpacing: '-0.025em',
    },
    label: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      marginRight: 'var(--space-2)',
    },
    value: {
      color: 'var(--color-text-secondary)',
      fontWeight: 'var(--font-weight-normal)',
    },
    loader: {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid var(--color-border-light)',
      borderTop: '2px solid var(--color-primary)',
      borderRadius: 'var(--radius-full)',
      animation: 'spin 1s linear infinite',
      marginRight: 'var(--space-2)',
    },
    progressWrapper: {
      marginTop: 'var(--space-4)',
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-md)',
      backgroundColor: 'var(--color-bg-muted)',
      border: '1px solid var(--color-border-light)',
    },
    progressTrack: {
      height: '6px',
      backgroundColor: 'var(--color-border-light)',
      borderRadius: 'var(--radius-full)',
      overflow: 'hidden',
    },
    progressBar: {
      height: '100%',
      backgroundColor: 'var(--color-primary)',
      transition: 'width 0.4s ease',
    },
    progressText: {
      marginTop: 'var(--space-2)',
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-secondary)',
      fontWeight: 'var(--font-weight-semibold)',
    },
    summaryBox: {
      marginTop: 'var(--space-4)',
      padding: 'var(--space-3)',
      backgroundColor: 'rgba(16, 185, 129, 0.05)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid rgba(16, 185, 129, 0.2)',
    },
    categoryBox: {
      marginTop: 'var(--space-4)',
      padding: 'var(--space-3)',
      backgroundColor: 'rgba(16, 185, 129, 0.05)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid rgba(16, 185, 129, 0.2)',
    },
    perfBox: {
      marginTop: 'var(--space-4)',
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-info-light)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid rgba(14, 165, 233, 0.3)',
    },
    categoryItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 'var(--space-2) 0',
      borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {onBack && (
            <button 
              onClick={onBack}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border-default)',
                borderRadius: 'var(--radius-md)',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ← Back
            </button>
          )}
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-semibold)',
            margin: 0,
            color: 'var(--color-text-primary)',
            letterSpacing: '-0.025em',
          }}>
            Upload Bank Statement
          </h2>
        </div>
        <button
          onClick={() => {
            if (!lastBatchCreatedAt) {
              setError('No statement batch to undo');
              return;
            }
            setShowUndoConfirm(true);
          }}
          disabled={undoLoading || !lastBatchCreatedAt}
          title={!lastBatchCreatedAt ? 'No recent statement upload to undo' : undefined}
          style={{
            ...styles.button,
            backgroundColor: 'var(--color-error)',
            color: 'var(--color-text-inverse)',
            marginTop: 0,
            padding: 'var(--space-2) var(--space-4)',
            fontSize: 'var(--font-size-xs)',
            whiteSpace: 'nowrap',
            ...(undoLoading || !lastBatchCreatedAt ? styles.disabledButton : {}),
          }}
          onMouseEnter={(e) => {
            if (!undoLoading && lastBatchCreatedAt) {
              e.target.style.backgroundColor = '#d2342a';
            }
          }}
          onMouseLeave={(e) => {
            if (!undoLoading && lastBatchCreatedAt) {
              e.target.style.backgroundColor = 'var(--color-error)';
            }
          }}
        >
          {undoLoading ? '⏳ Undoing...' : 'Undo Last'}
        </button>
      </div>

      {!preview ? (
        <div
          style={styles.uploadArea}
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-primary)';
            e.currentTarget.style.backgroundColor = 'var(--color-primary-subtle)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-default)';
            e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)';
          }}
        >
          <div style={styles.uploadIcon}>🏦</div>
          <p style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)',
            margin: `0 0 var(--space-2) 0`
          }}>
            Upload Statement or Screenshot
          </p>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 0
          }}>
            Upload a bank or credit card statement (JPG, PNG, PDF - up to 10MB)
          </p>
          <p style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)',
            marginTop: 'var(--space-2)',
            fontStyle: 'italic',
            margin: `var(--space-2) 0 0 0`
          }}>
            All transactions will be automatically extracted and imported
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        <>
          <div style={styles.preview}>
            {preview === 'pdf' ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                backgroundColor: 'var(--color-bg-muted)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📄</div>
                <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--color-text-primary)', letterSpacing: '-0.011em' }}>
                  {selectedFile?.name}
                </p>
                <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  Statement PDF Ready to Upload
                </p>
              </div>
            ) : (
              <img src={preview} alt="Statement preview" style={styles.previewImage} />
            )}
          </div>

          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              onClick={handleUpload}
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(loading ? styles.disabledButton : {}),
              }}
            >
              {loading && <div style={styles.loader}></div>}
              {loading ? 'Analyzing Statement...' : 'Upload & Parse Statement'}
            </button>
            <button
              onClick={handleReset}
              style={{
                ...styles.button,
                ...(loading ? {
                  ...styles.primaryButton,
                  backgroundColor: '#dc2626',
                  color: 'white',
                } : styles.secondaryButton),
              }}
            >
              {loading ? 'Cancel Parsing' : 'Cancel'}
            </button>
          </div>

          {(loading || progressPercent > 0) && (
            <div style={styles.progressWrapper}>
              <div style={styles.progressTrack}>
                <div
                  style={{
                    ...styles.progressBar,
                    width: `${progressPercent}%`,
                  }}
                />
              </div>
              <div style={styles.progressText}>
                <span>{loading ? 'Processing statement…' : 'Completed'}</span>
                <span>{elapsedSeconds}s</span>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div style={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Year Input Modal */}
      {showYearPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 'var(--z-modal)',
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg-base)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-8)',
            maxWidth: '400px',
            boxShadow: 'var(--shadow-xl)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-bold)',
              marginBottom: 'var(--space-4)',
              color: 'var(--color-text-primary)',
              margin: `0 0 var(--space-4) 0`
            }}>
              What year is this statement from?
            </h3>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-6)',
              margin: `0 0 var(--space-6) 0`
            }}>
              We couldn't determine the year from the filename or statement content. Please specify which year this statement covers.
            </p>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              placeholder={new Date().getFullYear().toString()}
              value={userYear}
              onChange={(e) => setUserYear(e.target.value)}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-default)',
                border: '1px solid var(--color-border-default)',
                fontSize: 'var(--font-size-base)',
                marginBottom: 'var(--space-6)',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && userYear) {
                  handleUpload();
                }
              }}
            />
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowYearPrompt(false);
                  setUserYear('');
                }}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  margin: 0,
                  marginTop: 0
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--color-bg-subtle)')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = 'var(--color-bg-muted)')}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!userYear}
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  margin: 0,
                  marginTop: 0,
                  opacity: !userYear ? 0.5 : 1,
                  cursor: !userYear ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => !userYear || (e.target.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => !userYear || (e.target.style.backgroundColor = 'var(--color-primary)')}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div style={styles.result}>
          <h3 style={{...styles.resultTitle}}>
            ✅ Statement Imported Successfully!
          </h3>
          <div style={styles.resultItem}>
            <span style={styles.label}>Transactions Imported:</span>
            <span style={{
              ...styles.value,
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#166534'
            }}>
              {result.imported}
            </span>
          </div>

          {result.failed > 0 && (
            <div style={{
              ...styles.resultItem,
              padding: '8px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              color: '#991b1b'
            }}>
              <span style={styles.label}>Failed to Parse:</span>
              <span style={styles.value}>{result.failed} transaction(s)</span>
            </div>
          )}

          {result.summary && (
            <>
              <div style={styles.summaryBox}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '10px' }}>
                  📊 Summary:
                </div>
                {(() => {
                  const stmtCurrency = result.detectedCurrency || result.summary?.statementCurrency;
                  const isForeignCurrency = stmtCurrency && stmtCurrency !== userCurrency;
                  return (
                    <>
                      <div style={styles.resultItem}>
                        <span style={styles.label}>Total Amount:</span>
                        <span style={{
                          ...styles.value,
                          fontWeight: 'bold',
                          color: '#166534',
                          fontSize: '16px'
                        }}>
                          {isForeignCurrency
                            ? formatCurrency(result.summary.totalAmountOriginal || result.summary.totalAmount || 0, stmtCurrency)
                            : formatCurrency(result.summary.totalAmount || 0, userCurrency)}
                        </span>
                      </div>
                      {isForeignCurrency && (
                        <div style={styles.resultItem}>
                          <span style={styles.label}>Converted Total:</span>
                          <span style={{...styles.value, fontWeight: '600', color: '#166534' }}>
                            ≈ {formatCurrency(result.summary.totalAmount || 0, userCurrency)}
                          </span>
                        </div>
                      )}
                      <div style={styles.resultItem}>
                        <span style={styles.label}>Average per Transaction:</span>
                        <span style={{...styles.value, fontWeight: '600' }}>
                          {isForeignCurrency
                            ? formatCurrency(result.summary.averageAmountOriginal || result.summary.averageAmount || 0, stmtCurrency)
                            : formatCurrency(result.summary.averageAmount || 0, userCurrency)}
                        </span>
                      </div>
                    </>
                  );
                })()}
                {result.summary.dateRange && (
                  <div style={styles.resultItem}>
                    <span style={styles.label}>Date Range:</span>
                    <span style={styles.value}>
                      {formatDateDisplay(result.summary.dateRange.start) || 'N/A'} to {formatDateDisplay(result.summary.dateRange.end) || 'N/A'}
                    </span>
                  </div>
                )}
              </div>

              {(() => {
                const stmtCurrency = result.detectedCurrency || result.summary?.statementCurrency;
                const isForeignCurrency = stmtCurrency && stmtCurrency !== userCurrency;
                const breakdown = isForeignCurrency
                  ? (result.summary.categoryBreakdownOriginal || result.summary.categoryBreakdown)
                  : result.summary.categoryBreakdown;
                return breakdown && Object.keys(breakdown).length > 0 && (
                  <div style={styles.categoryBox}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '10px' }}>
                      📂 By Category:
                    </div>
                    {Object.entries(breakdown).map(([category, amount]) => (
                      <div key={category} style={styles.categoryItem}>
                        <span style={{ color: '#166534', fontWeight: '500', textTransform: 'capitalize' }}>
                          {category}
                        </span>
                        <span style={{ color: '#166534', fontWeight: '700' }}>
                          {formatCurrency(amount, isForeignCurrency ? stmtCurrency : userCurrency)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          <div style={styles.perfBox}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1d4ed8', marginBottom: '10px' }}>
              ⚡ Processing Metrics:
            </div>
            {result.timings ? (
              <>
                <div style={styles.resultItem}>
                  <span style={styles.label}>OCR Time:</span>
                  <span style={styles.value}>{result.timings.ocr} ms</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.label}>OCR Throttles:</span>
                  <span style={styles.value}>{result.timings.ocrThrottles ?? 0}</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.label}>OCR Retries:</span>
                  <span style={styles.value}>{result.timings.ocrRetries ?? 0}</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.label}>Claude:</span>
                  <span style={styles.value}>{result.timings.claude} ms</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.label}>Embeddings:</span>
                  <span style={styles.value}>{result.timings.embeddings} ms</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.label}>Database:</span>
                  <span style={styles.value}>{result.timings.database} ms</span>
                </div>
              </>
            ) : (
              <div style={styles.resultItem}>
                <span style={styles.value}>
                  No timing data received from the server.
                </span>
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-6)'
          }}>
            <button
              onClick={handleReset}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                flex: 1,
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--color-primary-hover)')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = 'var(--color-primary)')}
            >
              Upload Another Statement
            </button>
            <button
              onClick={() => {
                if (!lastBatchCreatedAt) {
                  setError('No statement batch to undo');
                  return;
                }
                setShowUndoConfirm(true);
              }}
              disabled={undoLoading || !lastBatchCreatedAt}
              title={!lastBatchCreatedAt ? 'No recent statement upload to undo' : undefined}
              style={{
                ...styles.button,
                backgroundColor: 'var(--color-error)',
                color: 'var(--color-text-inverse)',
                flex: 1,
                ...((undoLoading || !lastBatchCreatedAt) ? styles.disabledButton : {}),
              }}
              onMouseEnter={(e) => {
                if (!undoLoading && lastBatchCreatedAt) {
                  e.target.style.backgroundColor = '#d2342a';
                }
              }}
              onMouseLeave={(e) => {
                if (!undoLoading && lastBatchCreatedAt) {
                  e.target.style.backgroundColor = 'var(--color-error)';
                }
              }}
            >
              {undoLoading ? '⏳ Undoing...' : '↩️ Undo'}
            </button>
          </div>
        </div>
      )}

      {/* Undo Confirmation Modal */}
      {showUndoConfirm && lastBatchCreatedAt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 'var(--z-modal)',
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg-base)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-8)',
            maxWidth: '400px',
            boxShadow: 'var(--shadow-xl)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-bold)',
              marginBottom: 'var(--space-4)',
              color: 'var(--color-text-primary)',
              margin: `0 0 var(--space-4) 0`
            }}>
              Undo Statement Upload?
            </h3>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-6)',
              margin: `0 0 var(--space-6) 0`
            }}>
              This will delete all {result?.imported} transactions that were imported from this statement. This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowUndoConfirm(false)}
                disabled={undoLoading}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  margin: 0,
                  marginTop: 0
                }}
                onMouseEnter={(e) => !undoLoading && (e.target.style.backgroundColor = 'var(--color-bg-subtle)')}
                onMouseLeave={(e) => !undoLoading && (e.target.style.backgroundColor = 'var(--color-bg-muted)')}
              >
                Cancel
              </button>
              <button
                onClick={handleUndo}
                disabled={undoLoading}
                style={{
                  ...styles.button,
                  backgroundColor: 'var(--color-error)',
                  color: 'var(--color-text-inverse)',
                  margin: 0,
                  marginTop: 0,
                  padding: 'var(--space-3) var(--space-6)',
                  opacity: undoLoading ? 0.5 : 1,
                  cursor: undoLoading ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => !undoLoading && (e.target.style.backgroundColor = '#d2342a')}
                onMouseLeave={(e) => !undoLoading && (e.target.style.backgroundColor = 'var(--color-error)')}
              >
                {undoLoading ? '⏳ Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default StatementUpload;
